interface ReturnsPolicyTemplateProps {
    data: {
        Professional: {
            Title: string;
            Link: string;
        };
    };
}

const sectionHeading =
    'mt-8 mb-3 pb-1 text-sm font-semibold border-b border-gray-200';

const ReturnsPolicyTemplate = ({
    data: { Professional },
}: ReturnsPolicyTemplateProps) => {
    const contactURL = `${Professional?.Link}/contact`;

    return (
        <div className="max-w-[800px] text-sm leading-relaxed text-gray-600">
            <div className="mb-6 rounded-lg bg-gray-50 px-5 py-4 text-sm leading-relaxed text-gray-700">
                Please note, your transaction is between yourself (the
                purchaser), and the seller of the product (the merchant).
                ArchiPro does not hold any product, and does not accept product
                returns. This Returns and Refund policy is between you and the
                merchant,{' '}
                <span className="font-semibold">{Professional.Title}</span>.
            </div>

            <h3 className={sectionHeading}>Returns</h3>

            <h4 className="mb-2 mt-5 text-sm font-semibold">
                Non Refundable Items
            </h4>
            <p className="mt-2">Items that are not returnable include:</p>
            <ul className="mb-4 mt-2 list-disc space-y-1.5 pl-6 text-gray-600">
                <li>
                    Products that have been purchased while on sale and priced
                    at a reduced retail price
                </li>
                <li>Bespoke and custom made products</li>
                <li>
                    Products that are indent orders or made to order where the
                    merchant orders in the product specifically for you
                </li>
                <li>Gift vouchers</li>
            </ul>

            <p className="mt-3">
                {Professional.Title} will accept returns for faulty or defective
                items in line with the Consumer Guarantees Act 1993 as long as
                the fault or defect is reported within 48 hours of receiving the
                item. You cannot claim if:
            </p>
            <ul className="mb-4 mt-2 list-disc space-y-1.5 pl-6 text-gray-600">
                <li>
                    You used the product in a way that was outside the intended
                    purpose/use.
                </li>
                <li>
                    You broke or lost the product, accidental or non-accidental
                    damage.
                </li>
                <li>
                    You modified the product, and this is what caused the issue.
                </li>
                <li>
                    Normal wear and tear within the reasonable life of the
                    product.
                </li>
                <li>
                    If the fault falls outside of the standard warranty period
                    of the product.
                </li>
            </ul>

            <h3 className={sectionHeading}>Requesting a Return</h3>
            <p className="mt-3">
                If your item is faulty please notify within 48 hours of
                receiving the item to arrange the return of the item.
            </p>
            <p className="mt-3">
                To be eligible for a return, your items must be unused, in their
                original condition and original packaging.
            </p>
            <p className="mt-3">
                You can request a return of your product/s by contacting us
                directly via our{' '}
                <a
                    href={contactURL}
                    className="font-medium text-gray-800 underline underline-offset-2 hover:text-black"
                >
                    contact page
                </a>
                . Alternatively, you can request a refund via your ArchiPro
                Order manager:
            </p>
            <ol className="mb-4 mt-3 list-decimal space-y-2 pl-6 text-gray-600">
                <li>Log in to your ArchiPro account</li>
                <li>Head to the &apos;Orders&apos; tab within your profile.</li>
                <li>
                    Select the order with the product/s you would like to
                    return.
                </li>
                <li>
                    Select the items from your order that you would like to
                    return.
                    <br />
                    <span className="mt-1 inline-block text-xs text-gray-500">
                        <strong className="text-gray-700">Note:</strong> Items
                        can only be returned if the order has been confirmed and
                        marked as processed.
                    </span>
                </li>
                <li>
                    Click the &apos;Return Items&apos; button in the bottom
                    right of the &apos;order summary&apos;.
                </li>
                <li>
                    Enter a reason for returning the items, select your quantity
                    of products you wish to return, and add any relevant photos
                    you would like to include.
                </li>
                <li>
                    We&apos;ll make contact with you and organise a suitable
                    time to collect the product. Costs of shipping and or return
                    may not be covered in some cases.
                </li>
            </ol>
            <p className="mt-3">
                If it has a minor fault, we may choose to repair the item,
                replace it or refund your money. If it is a major fault,
                it&apos;s your choice whether you opt for a replacement or
                refund.
            </p>
            <p className="mt-3">
                For additional information on consumer rights and the{' '}
                <em>Consumer Guarantees Act</em>,{' '}
                <a
                    href="https://www.consumerprotection.govt.nz/general-help/consumer-laws/consumer-guarantees-act"
                    target="_blank"
                    rel="noopener"
                    className="font-medium text-gray-800 underline underline-offset-2 hover:text-black"
                >
                    click here
                </a>
                .
            </p>

            <h3 className={sectionHeading}>Packaging Your Return</h3>
            <p className="mt-3">
                Please do not write on the product packaging as your return
                request may be rejected and the refund declined.
            </p>
            <p className="mt-3">
                Ensure the item is wrapped to protect both the product and the
                packaging and the address is clearly visible.
            </p>

            <h3 className={sectionHeading}>Returned Items Process</h3>
            <p className="mt-3">
                Once we receive your item, we&apos;ll notify you that we&apos;ve
                received it. Then we&apos;ll inspect it and decide whether we
                will repair it, replace it or refund your money.
            </p>
            <p className="mt-3">
                We will immediately notify you on the status of your returned
                item after inspecting it.
            </p>
            <p className="mt-3">
                If a return is approved, we will initiate a refund to your
                credit card (or original method of payment).
            </p>
            <p className="mt-3">
                You will receive the credit within a certain amount of days,
                depending on your card issuer&apos;s policies.
            </p>

            <h3 className={sectionHeading}>Shipping Costs</h3>
            <p className="mt-3">
                If your product is faulty or doesn&apos;t match its description,
                you are entitled to have the cost of return postage refunded to
                you.
            </p>
            <p className="mt-3">
                Please be aware, for some products, the original shipping and
                return shipping costs may not be refundable.
            </p>
            <p className="mt-3">
                You are responsible for the return shipping cost for change of
                mind purchases that are accepted for return.
            </p>

            <h3 className={sectionHeading}>Contact Us</h3>
            <p className="mt-3">
                If you have any questions on how to return your item to us,
                contact us via our{' '}
                <a
                    href={contactURL}
                    className="font-medium text-gray-800 underline underline-offset-2 hover:text-black"
                >
                    contact page
                </a>
                .
            </p>
        </div>
    );
};

export default ReturnsPolicyTemplate;
